import { addToast, Button, Modal } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../../components/ozmoit/common';
import { getSelectedOption } from '../../../../../utils/selectFormat';
import { postCreateObservationAction } from '../actions';
import { useTexts } from '../hooks/useTexts';
import { StudentObservationFormLoaderData } from '../Loaders/StudentObservationFormLoader';
import { StudentObservationsDataLoader } from '../Loaders/StudentObservationsLoader';
import { StudentObservations } from '../types';
import { FormData, StudentObservationForm } from './StudentObservationForm';

export type StudentObservationModalMethods = {
  open: (row?: StudentObservations, onlySee?: boolean) => void;
  close: () => void;
};

type StudentObservationModalProps = {
  studentId: string;
  onClose?: () => void;
};

const defaultForm = {
  type: null,
  course: null,
  observation: '',
  file: null,
};

const StudentObservationCreateModal = (
  { studentId, onClose }: StudentObservationModalProps,
  ref: Ref<StudentObservationModalMethods>,
) => {
  const data = StudentObservationFormLoaderData();
  const { refresh } = StudentObservationsDataLoader();

  const { modal: texts } = useTexts();
  const [open, setOpen] = useState<boolean>(false);
  const [onlySee, setOnlySee] = useState<boolean>(false);

  const forms = useForm<FormData>({ defaultValues: defaultForm });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = forms;

  const handleOpen = useCallback(
    (row?: StudentObservations, onlySee?: boolean) => {
      reset({
        type: getSelectedOption(row?.type, data.types),
        course: row?.course?.id
          ? getSelectedOption(row?.course?.id, data.courses)
          : null,
        observation: row?.observation,
        file: row?.file,
      });
      onlySee && setOnlySee(onlySee);
      setOpen(true);
    },
    [data, reset],
  );

  const handleClose = useCallback(() => {
    reset({});
    onClose?.();
    setOpen(false);
    setOnlySee(false);
  }, [onClose, reset]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const { action: handleClickSave } = useActionKitImp(
    postCreateObservationAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: texts.success,
        });
        handleClose();
        refresh();
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: texts.error,
          });
        },
      },
    },
  );

  const onSubmit = useCallback(
    async (values: FormData) => {
      const payload: any = {
        type: values.type?.value,
        courseId: values.course?.value,
        observation: values.observation?.trim() ?? null,
        file: values?.file,
      };
      await handleClickSave(Number(studentId), payload);
    },
    [studentId, handleClickSave],
  );

  return (
    <Modal isOpen={open} toggle={() => handleClose()} size="lg">
      <Container fluid>
        <h1 className="text-dark fs-20 mt-3 mb-4 text-center">
          {onlySee ? texts.titleSee : texts.title}
        </h1>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...forms}>
            <Row>
              <StudentObservationForm disabled={onlySee} />
              <Col xs={12} className="py-4" />
              {onlySee ? (
                <Col xs={12} md={12}>
                  <Button
                    outlined
                    text={texts.buttons.close}
                    fullwidth
                    onClick={() => handleClose()}
                    className="mb-3"
                  />
                </Col>
              ) : (
                <>
                  <Col xs={12} md={6}>
                    <Button
                      outlined
                      text={texts.buttons.cancel}
                      fullwidth
                      onClick={() => handleClose()}
                      className="mb-3"
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Button
                      type="submit"
                      text={texts.buttons.create}
                      className="mb-3"
                      fullwidth
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    />
                  </Col>
                </>
              )}
            </Row>
          </FormProvider>
        </form>
      </Container>
    </Modal>
  );
};

export default forwardRef(StudentObservationCreateModal);
